import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Archive03Icon,
  ArtificialIntelligence08Icon,
  FolderLibraryIcon,
  PropertyEditIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FormTypes } from 'constants/forms';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { Permissions } from 'constants/permissions';
import { useDocumentTitle } from 'hooks/document-title';
import AppPage from 'layouts/AppPage/AppPage';
import { Tab } from 'react-bootstrap';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { OverflowTabs } from 'components/Tabs/Tabs';

import { messagePopUp } from 'api/app-slice';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { archiveForm, deleteForm, getForms, restoreForm, saveForm } from 'api/forms-slice';
import { selectForms } from 'api/forms-slice';
import { getTeams } from 'api/teams-slice';
import { selectTeams } from 'api/teams-slice';

import styles from './Questionnaires.module.scss';
import { ArchivedForms } from './tabs/ArchivedForms/ArchivedForms';
import { CustomForms } from './tabs/CustomForms/CustomForms';
import { IQForms } from './tabs/IQForms/IQForms';
import { LibraryForms } from './tabs/LibraryForms/LibraryForms';

const Questionnaires = () => {
  useDocumentTitle('Questionnaires');
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { tab: targetTab, id } = useParams();

  const dispatch = useDispatch();

  const company = useSelector(selectCompany);
  const forms = useSelector(selectForms);
  const companyChecks = useSelector(selectCompanyChecks);
  const teams = useSelector(selectTeams);

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(1);

  const [referenceCheckDetails, setReferenceCheckDetails] = useState();

  const excludedQuestionnaireTypes = [
    FormTypes.QUESTIONNAIRE,
    FormTypes.REFEREE,
    FormTypes.CANDIDATE,
  ];

  const archivedForms = useMemo(() => {
    return Object.values(forms).reduce((acc, category) => {
      return acc.concat(
        category.filter(form => form.archived && !excludedQuestionnaireTypes.includes(form.type)),
      );
    }, []);
  }, [forms]);

  // Initialize
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: 'Questionnaires',
    });

    Promise.all([dispatch(getTeams()), dispatch(getForms())]).then(_ => {
      setLoading(false);
    });
  }, []);

  // Set tab
  useEffect(() => setTab(targetTab), [targetTab]);

  // Set custom reference enabled
  useEffect(() => {
    setReferenceCheckDetails(companyChecks.find(companyCheck => companyCheck.type === 'reference'));
  }, [companyChecks]);

  // Delete form
  const handleDeleteForm = async form => {
    setLoading(true);
    await dispatch(deleteForm(form));
    dispatch(
      messagePopUp({ text: 'Form successfully deleted', state: MESSAGE_STATE_SUCCESS, hide: true }),
    );
    setLoading(false);
  };

  const handleArchiveForm = async form => {
    setLoading(true);
    try {
      await dispatch(archiveForm(form));
      await dispatch(getForms());
      dispatch(
        messagePopUp({
          text: 'Form successfully archived',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreForm = async form => {
    setLoading(true);
    try {
      await dispatch(restoreForm(form));
      await dispatch(getForms());
      dispatch(
        messagePopUp({
          text: 'Form successfully restored',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForm = async formData => {
    setLoading(true);
    try {
      const { payload: response } = await dispatch(saveForm(formData));
      if (response.error) throw response.error.message;
      dispatch(
        messagePopUp({ text: 'Form successfully saved', state: MESSAGE_STATE_SUCCESS, hide: true }),
      );
      return response.result.form;
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const showError = error =>
    dispatch(messagePopUp({ text: error, state: MESSAGE_STATE_ERROR, hide: true }));

  return (
    <AppPage loading={loading} requiredPermissions={[Permissions.FormsWrite]}>
      <div className={styles.root}>
        <AppPage.Header
          title={t('questionnaires.questionnaires.title')}
          subtitle={t('questionnaires.questionnaires.subtitle')}
        />
        <OverflowTabs
          id="accountTabs"
          onSelect={key => navigate(`/questionnaires/${key}`)}
          activeKey={tab}
          className={cn('mt-12 mx-12', styles.tabs)}>
          {!!referenceCheckDetails && !referenceCheckDetails.configuration?.hide_public_library && (
            <Tab
              eventKey="library"
              title={
                <span className="flex items-center gap-2">
                  <HugeiconsIcon icon={FolderLibraryIcon} size="16" strokeWidth={2} /> Library
                </span>
              }>
              <LibraryForms forms={forms.library} />
            </Tab>
          )}
          {!!referenceCheckDetails && (
            <Tab
              eventKey="custom"
              title={
                <span className="flex items-center gap-2">
                  <HugeiconsIcon icon={PropertyEditIcon} size="16" strokeWidth={2} />{' '}
                  {t('questionnaires.custom.title')}
                </span>
              }>
              <CustomForms
                forms={(forms?.customs || []).filter(form => !form.archived)}
                onSave={handleSaveForm}
                onDelete={handleDeleteForm}
                onArchive={handleArchiveForm}
                copyFrom={forms.library.find(f => f.typeform_id === id)}
              />
            </Tab>
          )}
          {!!referenceCheckDetails && (
            <Tab
              eventKey="iq"
              title={
                <span className="flex items-center gap-2">
                  <HugeiconsIcon icon={ArtificialIntelligence08Icon} size="16" strokeWidth={2} />
                  Checkmate{' '}
                  <img src="assets/images/icons/iq_logo.svg" className="h-8 w-8" alt="iq" />
                </span>
              }>
              <IQForms forms={forms.intelligent} />
            </Tab>
          )}
          <Tab
            eventKey="archived"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={Archive03Icon} size="16" strokeWidth={2} />{' '}
                {t('questionnaires.archived.title')} ({archivedForms.length})
              </span>
            }>
            <ArchivedForms
              forms={archivedForms}
              teams={teams}
              onRestore={handleRestoreForm}
              onDelete={handleDeleteForm}
            />
          </Tab>
        </OverflowTabs>
      </div>
    </AppPage>
  );
};

export default Questionnaires;
